import React, { useState, useEffect, useRef } from "react";
import { FiFilter, FiChevronDown } from "react-icons/fi";
import "./FilterPanel.css";

/**
 * Filter button + drop-down panel used by the master pages (same look as the
 * Home page filter panel). Every change applies immediately — picking an option
 * or typing in search filters the list straight away; the panel stays open until
 * you click outside it, press Enter, or hit "Clear all".
 *
 *   <FilterPanel
 *     values={{ search, country: fCountry }}            // current filters
 *     emptyValues={{ search: "", country: "" }}         // what "Clear all" resets to
 *     onChange={(v) => { setSearch(v.search); setFCountry(v.country); setPage(1); }}
 *   >
 *     {(filters, set) => (
 *       <FilterField label="Country">
 *         <select value={filters.country} onChange={e => set({ country: e.target.value })}>…</select>
 *       </FilterField>
 *     )}
 *   </FilterPanel>
 *
 * `set(patch)` merges the patch into the current values and calls onChange right away.
 *
 * Optional:
 *   fullWidth — panel spans the whole action bar (the nearest positioned ancestor)
 *               and lays fields out in as many columns as fit, instead of `width`/`columns`.
 *   extra     — extra content shown under the fields (e.g. a "Show columns" section).
 *   onClear   — custom "Clear all" handler; defaults to onChange(emptyValues).
 */
const FilterPanel = ({
  values, emptyValues, onChange, onOpen, onClear, extra,
  title = "Filters", width = 320, columns = 1, fullWidth = false, children,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const activeCount = Object.values(values).filter(v => v !== "" && v != null).length;

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const toggle = () => {
    if (!open) onOpen?.(values);
    setOpen(o => !o);
  };

  const set = (patch) => onChange({ ...values, ...patch });

  const clearAll = () => {
    if (onClear) onClear(); else onChange(emptyValues);
    setOpen(false);
  };

  return (
    <div className={`filter-panel-wrap ${fullWidth ? "filter-panel-wrap--full" : ""}`} ref={wrapRef}>
      <button type="button" className={`action-btn filter-panel-btn ${activeCount ? "is-active" : ""}`} onClick={toggle}>
        <FiFilter size={13} />
        <span>Filter</span>
        {activeCount > 0 && <span className="filter-panel-count">{activeCount}</span>}
        <FiChevronDown size={12} className={`filter-panel-chevron ${open ? "is-open" : ""}`} />
      </button>

      {open && (
        <form className={`filter-panel ${fullWidth ? "filter-panel--full" : ""}`} style={fullWidth ? undefined : { width }}
          onSubmit={(e) => { e.preventDefault(); setOpen(false); }}>
          <div className="filter-panel-title">{title}</div>
          <div className="filter-panel-fields"
            style={fullWidth ? undefined : { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {children(values, set)}
          </div>
          {extra}
          <div className="filter-panel-footer">
            <button type="button" className="filter-panel-clear" onClick={clearAll}>Clear all</button>
          </div>
        </form>
      )}
    </div>
  );
};

// react-select styles for dropdowns inside the panel — same size, border, radius and
// colours as the panel's native inputs/selects (FilterPanel.css), so every field matches.
export const filterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 6,
    borderColor: state.isFocused ? "#1a2a4d" : "#ccc",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(26, 42, 77, 0.12)" : "none",
    backgroundColor: state.isDisabled ? "#f5f6f8" : "#fff",
    fontSize: 13,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    "&:hover": { borderColor: state.isFocused ? "#1a2a4d" : "#999" },
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 10px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#333" }),
  singleValue: (base, state) => ({ ...base, color: state.isDisabled ? "#aaa" : "#333" }),
  placeholder: (base, state) => ({ ...base, color: state.isDisabled ? "#aaa" : "#999" }),
  indicatorSeparator: () => ({ display: "none" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 9px 0 4px", color: "#999", "&:hover": { color: "#555" } }),
  clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#999", "&:hover": { color: "#d53e40" } }),
  menu: (base) => ({
    ...base,
    marginTop: 4,
    borderRadius: 6,
    border: "1px solid #e0e0e0",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuList: (base) => ({ ...base, padding: "4px 0" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
    color: state.isSelected ? "#1a2a4d" : "#333",
    fontWeight: state.isSelected ? 600 : 400,
    backgroundColor: state.isSelected ? "#eef1f8" : state.isFocused ? "#f5f6f8" : "#fff",
    "&:active": { backgroundColor: "#eef1f8" },
  }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 12, color: "#aaa" }),
};

export const FilterField = ({ label, children, wide }) => (
  <div className="filter-panel-field" style={wide ? { gridColumn: "1 / -1" } : undefined}>
    <span className="filter-panel-label">{label}</span>
    {children}
  </div>
);

export default FilterPanel;
